import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState } from "@/components/common";
import CompanyProfile, { ReassessBanner } from "@/components/CompanyProfile";
import AssessmentPanel from "@/components/AssessmentPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import RequestsPanel from "@/components/RequestsPanel";
import UsersPanel from "@/components/UsersPanel";
import TaskTable from "@/components/TaskTable";
import TaskDrawer from "@/components/TaskDrawer";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { api, errMsg, MGMT_LABEL } from "@/lib/api";

function CompanyTasks({ cid }) {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/tasks", { params: { tenant_id: cid } }).then((r) => setTasks(r.data));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line
  return <><TaskTable tasks={tasks} onOpen={setOpen} showCompany={false} /><TaskDrawer taskId={open} onClose={() => setOpen(null)} onChanged={load} /></>;
}

export default function CompanyDetail() {
  const params = useParams();
  const { user, isStaff } = useAuth();
  const cid = params.id || user.tenant_id;
  const [c, setC] = useState(null);
  const load = () => api.get(`/companies/${cid}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line
  if (!c) return <EmptyState title="Loading company…" testId="company-loading" />;
  const showUsers = user.role !== "client_contributor";
  return (
    <div data-testid="company-detail">
      <PageHeader title={c.name} subtitle={`${c.legal_form || "Company"} · TIN ${c.tin || "—"} · ${MGMT_LABEL[c.management_model]}`} />
      {isStaff && <div className="mb-4"><WhatsAppButton kind="company" id={c.id} /></div>}
      <ReassessBanner company={c} onDone={load} />
      <Tabs defaultValue="profile">
        <TabsList className="mb-4 h-auto flex-wrap justify-start">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="obligations" data-testid="tab-obligations">Obligations</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
          {showUsers && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
        </TabsList>
        <TabsContent value="profile"><CompanyProfile key={c.updated_at || c.id} company={c} onSaved={setC} /></TabsContent>
        <TabsContent value="obligations"><AssessmentPanel cid={cid} /></TabsContent>
        <TabsContent value="tasks"><CompanyTasks cid={cid} /></TabsContent>
        <TabsContent value="documents"><DocumentsPanel tenantId={cid} /></TabsContent>
        <TabsContent value="requests"><RequestsPanel tenantId={cid} /></TabsContent>
        {showUsers && <TabsContent value="users"><UsersPanel tenantId={isStaff ? cid : undefined} /></TabsContent>}
      </Tabs>
    </div>
  );
}
